import * as React from "react";
import { Outlet, ScrollRestoration, useSearchParams } from "react-router-dom";
import { Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { BrandMark } from "@/components/shared/brand-mark";
import { CornerRipples, SidebarRipples } from "@/components/shared/brand-art";
import { ThemeToggle } from "@/components/shared/theme-toggle";
import { NotificationBell } from "@/components/shared/notifications/notification-bell";
import { TicketTopbarButton } from "@/components/shared/tickets/ticket-topbar-button";
import { WhatsAppTopbarButton } from "@/components/shared/whatsapp/whatsapp-topbar-button";
import { useCustomerPanel } from "@/components/shared/customers/customer-panel-params";
import { useChampPanel } from "@/components/shared/champs/champ-panel-params";
import { GlobalSearch } from "./global-search";
import { SidebarNav } from "./sidebar-nav";
import { UserMenu } from "./user-menu";
import { Breadcrumbs } from "./breadcrumbs";
import { usePageArrival } from "./use-page-arrival";
import { useIsMobile } from "@/hooks/use-media-query";
import { usePermissions } from "@/hooks/use-permissions";
import { useWhatsAppLive } from "@/hooks/use-whatsapp-live";
import { Permission } from "@/lib/permissions";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "carselona-admin-sidebar-collapsed";

// The WhatsApp panel and its conversation view are their own chunk, fetched
// the first time a `?wa=` link is opened — not on every page load.
const WhatsAppDock = React.lazy(() =>
  import("@/components/shared/whatsapp/whatsapp-dock").then((module) => ({
    default: module.WhatsAppDock,
  })),
);

function WhatsAppDockSlot() {
  const [params] = useSearchParams();
  if (!params.get("wa")) return null;
  return (
    <React.Suspense fallback={null}>
      <WhatsAppDock />
    </React.Suspense>
  );
}

// The ticket panel is its own chunk too, fetched on the first `?tk=` link.
const TicketDock = React.lazy(() =>
  import("@/components/shared/tickets/ticket-dock").then((module) => ({
    default: module.TicketDock,
  })),
);

function TicketDockSlot() {
  const [params] = useSearchParams();
  if (!params.get("tk")) return null;
  return (
    <React.Suspense fallback={null}>
      <TicketDock />
    </React.Suspense>
  );
}

// The customer panel is its own chunk, fetched the first time a `?customer=` link
// is opened (a name, the search, a shared link).
const CustomerPanel = React.lazy(() =>
  import("@/components/shared/customers/customer-panel").then((module) => ({
    default: module.CustomerPanel,
  })),
);

function CustomerPanelSlot() {
  const { customerId } = useCustomerPanel();
  if (!customerId) return null;
  return (
    <React.Suspense fallback={null}>
      <CustomerPanel />
    </React.Suspense>
  );
}

// The champ panel, likewise: fetched the first time a `?champ=` link is opened.
const ChampPanel = React.lazy(() =>
  import("@/components/shared/champs/champ-panel").then((module) => ({
    default: module.ChampPanel,
  })),
);

function ChampPanelSlot() {
  const { champId } = useChampPanel();
  if (!champId) return null;
  return (
    <React.Suspense fallback={null}>
      <ChampPanel />
    </React.Suspense>
  );
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * The authed layout: a fixed navy sidebar (240px, collapsible to a 56px icon
 * rail) holding the logo, the grouped nav and who is signed in; a 48px top bar
 * holding the sidebar toggle, the breadcrumb and the theme menu; content
 * capped at screen-2xl. Dimensions come from the skill via named Tailwind
 * spacing tokens.
 *
 * Below 768px the sidebar becomes a slide-over drawer and the rail collapse
 * is irrelevant, so the toggle is replaced by a menu button.
 */
export function AppShell() {
  // The one place permissions are polled, so an owner's change reaches an
  // open session within a minute. Every other caller reads the same cache.
  const { can } = usePermissions({ poll: true });
  // WhatsApp's one polling loop, its top-bar badge and its docked panel —
  // here because they serve every page.
  const whatsapp = can(Permission.WHATSAPP_VIEW);
  useWhatsAppLive(whatsapp);
  const tickets = can(Permission.TICKETS_VIEW);
  const customers = can(Permission.CUSTOMERS_VIEW);
  const champs = can(Permission.CHAMPS_VIEW);
  // The top-bar search finds customers, champs and tickets; either person key admits it.
  const search = customers || champs;
  const mainRef = usePageArrival();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = React.useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const toggleCollapsed = () => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        // Preference just won't persist.
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:shadow-sm"
      >
        Skip to content
      </a>

      {/* Desktop sidebar */}
      {!isMobile ? (
        <aside
          // `dark`: the sidebar is navy in both themes — the app icon's own
          // gradient — so it takes the dark tokens and everything in it
          // (text, icons, rules, the logo's white "DAILY") follows.
          className={cn(
            "dark fixed inset-y-0 left-0 isolate z-30 flex flex-col overflow-hidden bg-brand-navy text-sidebar-foreground transition-[width] duration-200",
            collapsed ? "w-sidebar-collapsed" : "w-sidebar",
          )}
        >
          <SidebarRipples className="absolute inset-x-0 bottom-0 -z-10 h-72 w-full" />
          <div
            className={cn(
              "flex h-topbar shrink-0 items-center",
              collapsed ? "justify-center px-2" : "px-4",
            )}
          >
            <BrandMark showWordmark={!collapsed} />
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            <SidebarNav collapsed={collapsed} />
          </div>

          <div className="border-t border-border p-2">
            <UserMenu collapsed={collapsed} />
          </div>
        </aside>
      ) : null}

      <div
        className={cn(
          "flex min-h-screen flex-col transition-[padding] duration-200",
          !isMobile && (collapsed ? "pl-sidebar-collapsed" : "pl-sidebar"),
        )}
      >
        <header className="sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-background px-3 md:px-4">
          {isMobile ? (
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Open navigation">
                  <Menu aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                // The drawer's first control is the Find a screen box, and
                // focusing it would pop a phone's keyboard over the menu.
                onOpenAutoFocus={(event) => {
                  event.preventDefault();
                  (event.currentTarget as HTMLElement).focus();
                }}
                className="dark isolate flex flex-col gap-0 overflow-hidden bg-brand-navy p-0 text-sidebar-foreground"
              >
                <SidebarRipples className="absolute inset-x-0 bottom-0 -z-10 h-72 w-full" />
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SheetDescription className="sr-only">
                  Links to the sections of the admin panel
                </SheetDescription>
                <div className="flex h-topbar shrink-0 items-center px-4">
                  <BrandMark />
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  <SidebarNav onNavigate={() => setDrawerOpen(false)} />
                </div>
                <div className="border-t border-border p-2">
                  <UserMenu />
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              className="text-muted-foreground"
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" />
              ) : (
                <PanelLeftClose aria-hidden="true" />
              )}
            </Button>
          )}

          <Separator orientation="vertical" className="mx-1 h-4" />
          <Breadcrumbs />

          {search ? <GlobalSearch /> : null}

          <div className={cn("flex items-center gap-1", !search && "ml-auto")}>
            {tickets ? <TicketTopbarButton /> : null}
            {whatsapp ? <WhatsAppTopbarButton /> : null}
            <NotificationBell />
            <ThemeToggle />
          </div>
        </header>

        <main
          id="main-content"
          ref={mainRef}
          // Focusable by script only, so arriving on a page can move a screen
          // reader there; never a tab stop, and no ring on a mouse click.
          tabIndex={-1}
          className="relative isolate flex-1 focus:outline-none"
        >
          {/* Brand art in the canvas's top-right corner, behind the page
              title. Cards are opaque, so it never sits under data. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-80 overflow-hidden"
          >
            <CornerRipples className="absolute right-0 top-0 h-full w-auto max-w-full" />
          </div>
          <div className="mx-auto w-full max-w-screen-2xl px-4 py-5 md:px-6">
            <Outlet />
          </div>
        </main>
        {/* Back to a list returns to where it was scrolled, not the top. */}
        <ScrollRestoration />
      </div>
      {whatsapp ? <WhatsAppDockSlot /> : null}
      {tickets ? <TicketDockSlot /> : null}
      {customers ? <CustomerPanelSlot /> : null}
      {champs ? <ChampPanelSlot /> : null}
    </div>
  );
}
